import { z } from 'zod';

const baseSchema = z.object({
    name: z.string().min(1, 'Cohort name is required'),
    category_mode: z.enum(['new', 'existing']),
    patient_ids: z.array(z.union([z.string(), z.number()])).optional(),
    patient_filter: z.any().optional(), // Advanced filter query
    chat_filter: z.string().optional(), // Chat filter (substring from name when messageId exists)
    sql_query: z.string().optional(), // SQL query from chat message
    description: z.string().optional(),
    linked_cohort_ids: z.array(z.string()).optional(),
    message_id: z.string().optional(), // Message ID to fetch patients from SQL query
    clinical_trial_job_id: z.string().optional(), // Clinical trial job ID to fetch patients from
    random_patients: z.number().optional(), // Random selected patients count
    random_seed: z.number().optional(), // Random seed for deterministic random selection of patients
    include_elasticsearch_for_filters: z.boolean().optional(), // Silver+Bronze: include ES patients for diagnosis/procedure filters
});

// Extended rules depending on category mode
export const createCohortSchema = baseSchema
    .and(
        z.discriminatedUnion('category_mode', [
            z.object({
                category_mode: z.literal('new'),
                new_category_name: z.string().min(1, 'New category name is required'),
                category_id: z.string().optional(),
            }),
            z.object({
                category_mode: z.literal('existing'),
                category_id: z.string().min(1, 'Please select a category'),
                new_category_name: z.string().optional(),
            }),
        ])
    )
    .transform(data => ({
        name: data.name.trim(),
        is_new_category: data.category_mode === 'new',
        category_id: data.category_mode === 'existing' ? data.category_id : undefined,
        category_name: data.category_mode === 'new' ? data.new_category_name : undefined,
        patient_ids: (data.patient_ids ?? []).map(String),
        patient_filter: data.patient_filter,
        chat_filter: data.chat_filter,
        sql_query: data.sql_query,
        description: data?.description?.trim() || undefined,
        linked_cohorts: data.linked_cohort_ids ?? [],
        message_id: data.message_id,
        clinical_trial_job_id: data.clinical_trial_job_id,
        random_patients: data.random_patients,
        random_seed: data.random_seed,
        include_elasticsearch_for_filters: data.include_elasticsearch_for_filters,
    }));

export type CreateCohortFormInput = z.infer<typeof createCohortSchema>;
